import { useState, useEffect } from "react";

/**
 * Hook to track media query matches
 *
 * @param query - CSS media query string
 * @returns Boolean indicating if the query matches
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
 */
export function useMediaQuery(query: string): boolean {
	// Get initial match state
	const getMatch = (): boolean => {
		if (typeof window !== "undefined") {
			return window.matchMedia(query).matches;
		}
		return false;
	};

	const [matches, setMatches] = useState<boolean>(getMatch);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const mediaQuery = window.matchMedia(query);

		// Set initial value
		setMatches(mediaQuery.matches);

		// Create event handler
		const handler = (event: MediaQueryListEvent) => {
			setMatches(event.matches);
		};

		// Modern browsers
		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener("change", handler);
			return () => mediaQuery.removeEventListener("change", handler);
		}

		// Legacy browsers (Safari < 14)
		mediaQuery.addListener(handler);
		return () => mediaQuery.removeListener(handler);
	}, [query]);

	return matches;
}

/**
 * Hook to detect mobile devices
 */
export function useIsMobile(): boolean {
	return useMediaQuery("(max-width: 768px)");
}

/**
 * Hook to detect tablet devices
 */
export function useIsTablet(): boolean {
	return useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
}

/**
 * Hook to detect desktop devices
 */
export function useIsDesktop(): boolean {
	return useMediaQuery("(min-width: 1025px)");
}

/**
 * Hook to detect reduced motion preference
 */
export function usePrefersReducedMotion(): boolean {
	return useMediaQuery("(prefers-reduced-motion: reduce)");
}

/**
 * Hook to detect dark mode preference
 */
export function usePrefersDarkMode(): boolean {
	return useMediaQuery("(prefers-color-scheme: dark)");
}

/**
 * Hook to get window width
 */
export function useWindowWidth(): number {
	const [width, setWidth] = useState<number>(
		typeof window !== "undefined" ? window.innerWidth : 0
	);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const handleResize = () => {
			setWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return width;
}

/**
 * Hook to get window height
 */
export function useWindowHeight(): number {
	const [height, setHeight] = useState<number>(
		typeof window !== "undefined" ? window.innerHeight : 0
	);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const handleResize = () => {
			setHeight(window.innerHeight);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return height;
}
